import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';
import { message } from 'antd';
import dayjs from 'dayjs';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { productType, PublicStateEnhance } from 'utils/constant';
import { IDetailProps } from 'utils/types/saleTypes';
import { theme } from 'utils/style/theme';
import { inAppStoreApi } from 'apis/sales/inAppStoreApis';
import InputFlexBox from 'components/container/InputFlexBox';
import InputWrappingBox from 'components/container/InputWrappingBox';
import SubmitFlexBox from 'components/container/SubmitFlexBox';
import DefaultInput from 'components/input/DefaultInput';
import RangeDatePicker from 'components/input/RangeDatePicker';
import DefaultLayout from 'components/layout/DefaultLayout';
import DefaultSelect from 'components/select/DefaultSelect';
import InAppLocalizingForm from './InAppLocalizingForm';
import UtcTimer from 'components/etc/UtcTimer';
import TextFlexBox from 'components/container/TextFlexBox';
import CouponItemTable from './CouponItemTable';
import DefaultButton from 'components/button/DefaultButton';
import { useTranslation } from 'react-i18next';
import { productApis } from '../../../apis/package/productApis';
import { itemApis } from '../../../apis/package/itemApis';
import { IItem } from '../../../utils/types/storyTypes';
import { ICoupon } from '../../../utils/types/operationTypes';
import { couponApis } from '../../../apis/operation/couponApis';

/**
 * * 판매 관리 - 인앱 상점 관리 - 등록 & 수정 페이지
 */

const CouponInputPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation(); // Access state passed via navigate

  const dateFormat = 'YYYY-MM-DD HH:mm:00';
  const today = dayjs().format(dateFormat);
  const [coupon, setCoupon] = useState<ICoupon>(location.state.coupon);
  const [detailDailyData, setDetailDailyData] = useState<IDetailProps[]>([
    { key: 0, currency: 'gem', quantity: 0 },
  ]);
  const [items, setItems] = useState<IItem[]>([]);

  const fetchData = async () => {
    const itemRes = await itemApis.itemList(Number(coupon.project_id));
    setItems(itemRes.list);
  };

  /// input & radio event
  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setCoupon({ ...coupon!, [name]: value });
  };

  const handleDateChange = (dates, dateStrings) => {
    if (!dates) {
      setCoupon({
        ...coupon!,
        start_date: `${today}`,
        end_date: `${today}`,
      });
      return;
    }
    setCoupon({
      ...coupon!,
      start_date: `${dateStrings[0]}`,
      end_date: `${dateStrings[1]}`,
    });
  };

  const handleSubmit = async () => {
    if (coupon.coupon_name === '') {
      message.error('쿠폰명을 입력해주세요');
      return false;
    }

    if (coupon.coupon_type === 'keyword' && coupon.keyword === '') {
      message.error('키워드를 입력해주세요');
      return false;
    }

    if (!coupon.start_date || !coupon.end_date) {
      message.error('쿠폰 유효기간을 입력해주세요');
      return false;
    }

    if (coupon.rewards.length === 0) {
      message.error('아이템을 추가해주세요');
      return false;
    }

    for (const reward of coupon.rewards) {
      if (reward.quantity < 1) {
        message.error('수량을 입력해주세요');
        return false;
      }
    }

    const res = await couponApis.updateCoupon(coupon);

    if (res) {
      message.success('쿠폰이 수정되었습니다.');
    } else {
      message.error('쿠폰 수정에 실패했습니다.');
    }

    const newCoupon = res.coupon;
    setCoupon(newCoupon);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleCopyAllToClipboard = () => {
    const allSerials = coupon.serials!.map((v) => v.serial).join('\n'); // 각 시리얼 번호를 줄바꿈으로 연결
    navigator.clipboard
      .writeText(allSerials)
      .then(() => {
        message.success('모든 시리얼 번호가 클립보드에 복사되었습니다.');
      })
      .catch(() => {
        message.error('클립보드 복사에 실패했습니다.');
      });
  };
  useEffect(() => {
    fetchData();
    if (!coupon) {
      navigate(-1);
    }
  }, []);

  return (
    <DefaultLayout>
      <SubmitFlexBox>
        <>
          {coupon.coupon_type === 'serial' && (
            <DefaultButton typename={'sub'} onClick={handleCopyAllToClipboard}>
              {'시리얼 복사'}
            </DefaultButton>
          )}
          <DefaultButton onClick={handleGoBack}>
            {t('ifYouID.header.backButton')}
          </DefaultButton>
          <DefaultButton onClick={handleSubmit}>
            {t('ifYouID.header.submitButton')}
          </DefaultButton>
        </>
      </SubmitFlexBox>

      <InputFlexBox style={{ marginTop: '2.5rem' }}>
        <>
          {coupon && (
            <>
              {coupon.coupon_type === 'serial' && (
                <InputWrappingBox left>
                  {coupon.serials!.map((v, i) => (
                    <DefaultInput
                      disabled={true}
                      key={i}
                      title={`시리얼 번호 ${i + 1}`}
                      name='serials'
                      value={v.serial}
                      style={{
                        textDecoration: v.use ? 'line-through' : 'none',
                        backgroundColor: v.use ? '#f5f5f5' : 'white',
                        color: v.use ? '#999' : 'black',
                      }}
                    />
                  ))}
                </InputWrappingBox>
              )}
              <InputWrappingBox>
                <DefaultInput
                  title={'쿠폰명'}
                  name='coupon_name'
                  value={coupon.coupon_name}
                  onChange={handleInputChange}
                />

                <>
                  {coupon.coupon_type === 'keyword' && (
                    <DefaultInput
                      title={'키워드'}
                      name='keyword'
                      value={coupon.keyword}
                      onChange={handleInputChange}
                    />
                  )}
                </>

                <DefaultInput
                  title={'쿠폰 타입'}
                  disabled={true}
                  name='coupon_type'
                  value={coupon.coupon_type}
                  onChange={handleInputChange}
                />

                <>
                  {coupon.coupon_type === 'keyword' && (
                    <>
                      <DefaultInput
                        title={'발급 수량'}
                        disabled={true}
                        name='issue_count'
                        value={coupon.issue_count}
                      />

                      <DefaultInput
                        title={'남은 수량'}
                        disabled={true}
                        name='remain_keyword_count'
                        value={coupon.remain_keyword_count}
                      />
                    </>
                  )}
                </>

                <DateSection>
                  <div>
                    <RangeDatePicker
                      title={'쿠폰 유효기간'}
                      format={dateFormat}
                      showTime={true}
                      value={[
                        dayjs(coupon.start_date, dateFormat),
                        dayjs(coupon.end_date, dateFormat),
                      ]}
                      ranges={{
                        오늘: [dayjs(), dayjs()],
                        이번주: [
                          dayjs().startOf(today as any),
                          dayjs().endOf('week'),
                        ],
                        이번달: [
                          dayjs().startOf(today as any),
                          dayjs().endOf('month'),
                        ],
                      }}
                      onChange={handleDateChange}
                      style={{ paddingBottom: '0.5rem', paddingTop: '0.5rem' }}
                    />
                    <UtcTimer
                      onGrayBox={true}
                      startDate={coupon.start_date ? coupon.start_date : today}
                      endDate={coupon.end_date ? coupon.end_date : today}
                      style={{
                        marginTop: '0.2rem',
                        marginBottom: theme.margins.bottomInModal,
                      }}
                    />
                  </div>
                </DateSection>

                {/* 상품 종류에 따라 생기는 옵션 */}
                <TextFlexBox
                  style={{
                    display: 'block',
                    marginRight: '2rem',
                    marginBottom: theme.margins.bottomInPage,
                  }}
                >
                  <h6>{'아이템'}</h6>
                  <>
                    <CouponItemTable
                      items={items.filter((v) => v.item_type !== 'gift')}
                      coupon={coupon}
                      setCoupon={setCoupon as Dispatch<SetStateAction<ICoupon>>}
                    />
                  </>
                </TextFlexBox>
              </InputWrappingBox>
            </>
          )}
        </>
      </InputFlexBox>
    </DefaultLayout>
  );
};

export default CouponInputPage;

const DateSection = styled.div`
  display: flex;
  align-items: center;

  .option_wrap {
    margin-top: -1rem;
    display: flex;
  }

  .checkbox_wrap {
    display: flex;
    align-items: center;
    margin-left: 1rem;

    > p {
      margin-left: 0.5rem;
    }
  }
`;
